// Spec 45 component-behavior — cmp_ds_pagination.
//
// Page-window navigator. Renders prev / page buttons (with smart
// truncation to "1 … 5 6 7 … 99") / next. Wrapper is <nav
// aria-label> per spec; pages are <button>s; current page has
// aria-current='page'.
//
// Truncation algorithm:
//   * Always show first + last page.
//   * Show siblings around current page (default ±1; configurable).
//   * Insert "…" ellipsis where the gap exceeds 1.
//
// Keyboard: native button semantics. Arrow keys NOT intercepted
// (this is navigation chrome, not a list — Tab moves through pages).

import { forwardRef, useMemo, type HTMLAttributes } from "react";

import { cx } from "../lib/cx.ts";
import { isDevGuardActive } from "../lib/dev-guard.ts";

export interface PaginationProps extends HTMLAttributes<HTMLElement> {
  /** Total number of pages. Must be ≥ 1. */
  readonly totalPages: number;
  /** 1-indexed current page. */
  readonly currentPage: number;
  readonly onPageChange: (page: number) => void;
  /** Pages to show on each side of currentPage in the visible window. Defaults to 1. */
  readonly siblings?: number;
  /** Required nav-landmark accessible label (e.g., "Pagination", "Reports list pagination"). */
  readonly "aria-label"?: string;
  /** Override the prev/next button labels for i18n. */
  readonly previousLabel?: string;
  readonly nextLabel?: string;
  /**
   * Override the per-page button label for i18n. A formatter rather than a
   * string because the page number is interpolated, and word order around it
   * is not universal ("Go to page 3" / "3. sayfaya git").
   *
   * This prop exists because the component shipped with prev/next overridable
   * and this one hardcoded; the gap was invisible while <Pagination> had no
   * consumers, and surfaced the moment a Turkish screen mounted it (SEM-6363).
   */
  readonly pageLabel?: (page: number) => string;
}

type PageItem = number | "ellipsis-start" | "ellipsis-end";

function buildPageItems(total: number, current: number, siblings: number): PageItem[] {
  if (total <= 1) return [1];
  // Show all if total fits without truncation: first + (siblings*2 + 1) + last
  // + 2 ellipses worth of space. Threshold ~7 is the conventional value.
  const window = 5 + siblings * 2;
  if (total <= window + 2) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const items: PageItem[] = [1];
  const left = Math.max(2, current - siblings);
  const right = Math.min(total - 1, current + siblings);
  if (left > 2) items.push("ellipsis-start");
  for (let i = left; i <= right; i += 1) items.push(i);
  if (right < total - 1) items.push("ellipsis-end");
  items.push(total);
  return items;
}

export const Pagination = forwardRef<HTMLElement, PaginationProps>(
  (
    {
      totalPages,
      currentPage,
      onPageChange,
      siblings = 1,
      "aria-label": ariaLabel = "Pagination",
      previousLabel = "Previous page",
      nextLabel = "Next page",
      pageLabel = (page: number): string => `Go to page ${page}`,
      className,
      ...rest
    },
    ref,
  ) => {
    if (isDevGuardActive() && (totalPages < 1 || !Number.isInteger(totalPages))) {
      throw new Error(`<Pagination> totalPages must be a positive integer (got ${totalPages})`);
    }
    if (isDevGuardActive() && (currentPage < 1 || currentPage > totalPages)) {
      throw new Error(`<Pagination> currentPage must be in [1, ${totalPages}] (got ${currentPage})`);
    }

    const items = useMemo(
      () => buildPageItems(totalPages, currentPage, siblings),
      [totalPages, currentPage, siblings],
    );

    const isFirst = currentPage <= 1;
    const isLast = currentPage >= totalPages;

    return (
      <nav
        ref={ref}
        aria-label={ariaLabel}
        data-component="pagination"
        className={cx("nai-pagination", className)}
        {...rest}
      >
        <button
          type="button"
          data-component="pagination-prev"
          aria-label={previousLabel}
          disabled={isFirst}
          className="nai-pagination-prev"
          onClick={() => {
            if (!isFirst) onPageChange(currentPage - 1);
          }}
        >
          ‹
        </button>
        <ol data-component="pagination-list" className="nai-pagination-list">
          {items.map((item, idx) => {
            if (item === "ellipsis-start" || item === "ellipsis-end") {
              return (
                <li
                  key={item + idx}
                  data-component="pagination-ellipsis"
                  className="nai-pagination-ellipsis"
                  aria-hidden
                >
                  …
                </li>
              );
            }
            const isActive = item === currentPage;
            return (
              <li key={item} data-component="pagination-item">
                <button
                  type="button"
                  data-component="pagination-page"
                  data-active={isActive || undefined}
                  aria-current={isActive ? "page" : undefined}
                  aria-label={pageLabel(item)}
                  className="nai-pagination-page"
                  onClick={() => onPageChange(item)}
                >
                  {item}
                </button>
              </li>
            );
          })}
        </ol>
        <button
          type="button"
          data-component="pagination-next"
          aria-label={nextLabel}
          disabled={isLast}
          className="nai-pagination-next"
          onClick={() => {
            if (!isLast) onPageChange(currentPage + 1);
          }}
        >
          ›
        </button>
      </nav>
    );
  },
);
Pagination.displayName = "Pagination";
